import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Lock, Eye, EyeOff, Building2, AlertTriangle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientInviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchInvite = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client-invites/${token}`);
      setInvite(res.data);
    } catch (e) {
      setInviteError(e.response?.data?.detail || 'Invalid or expired invitation link.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInvite(); }, [fetchInvite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/client-invites/${token}/accept`, { password });
      toast.success('Account created — welcome to your Client Portal!');
      await login(invite.email, password);
      navigate('/client');
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Failed to create your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#eef0f3] flex flex-col" data-testid="client-invite-page">
      <header className="bg-[#14181f] text-white px-4 py-4 sm:py-6">
        <div className="max-w-xl mx-auto flex items-center justify-center gap-3 sm:gap-4">
          <img src="/san-logo.jpg" alt="S.A.N Security" className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-sm shadow-[0_0_25px_rgba(250,204,21,0.3)]" />
          <div className="text-center sm:text-left">
            <h1 className="font-barlow tracking-widest text-xl sm:text-3xl leading-none">
              S.A.N SECURITY <span className="text-primary">LTD</span>
            </h1>
            <p className="text-[10px] sm:text-sm tracking-[0.25em] text-primary uppercase mt-1 font-semibold">
              Client Staffing Portal
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-3 sm:px-6 py-4 sm:py-10">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-lg shadow-sm p-4 sm:p-8 animate-fadeUp">
          {loading ? (
            <p className="text-center text-sm text-zinc-500 py-8">Checking your invitation…</p>
          ) : inviteError ? (
            <div className="text-center py-6" data-testid="invite-error">
              <AlertTriangle className="w-8 h-8 text-red-500 mx-auto" />
              <p className="mt-3 text-sm text-zinc-700">{inviteError}</p>
            </div>
          ) : (
            <>
              <div className="text-center">
                <span className="w-12 h-12 rounded-full bg-[#14181f] flex items-center justify-center mx-auto">
                  <Building2 className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </span>
                <h2 className="mt-3 text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight">
                  Welcome, {invite.contact_name}
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Create your account for <strong className="text-zinc-800">{invite.company_name}</strong>
                </p>
                <p className="text-xs text-zinc-400 mt-0.5">{invite.email}</p>
              </div>

              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-sm font-medium text-zinc-800">Choose a password</Label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      id="password"
                      data-testid="invite-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimum 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pl-10 pr-10 h-11 bg-white border-zinc-300 text-zinc-900 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm" className="text-sm font-medium text-zinc-800">Confirm password</Label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                      id="confirm"
                      data-testid="invite-confirm-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      className="pl-10 h-11 bg-white border-zinc-300 text-zinc-900 rounded-md"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-md" data-testid="invite-form-error">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="invite-submit-btn"
                  disabled={submitting}
                  className="w-full h-11 bg-[#14181f] text-white hover:bg-[#232a35] rounded-md uppercase tracking-[0.2em] font-semibold"
                >
                  {submitting ? 'Creating account…' : 'Create Account'}
                </Button>

                <p className="text-[11px] text-zinc-500 flex items-start gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  Your account gives you access only to your own company's staffing portal.
                </p>
              </form>
            </>
          )}
        </div>
      </div>

      <footer className="bg-[#14181f] text-white px-4 py-2.5 text-center">
        <p className="text-[10px] sm:text-xs text-white/50">S.A.N Security Ltd · Your Safety. Our Priority.</p>
      </footer>
    </div>
  );
}
